// ---------- Types ----------

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  requiresReauth?: boolean;
  reauthProvider?: string;
  reauthUrl?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  authProvider?: string;
}

// ---------- Helpers ----------

const AUTH_BASE_URL = '/api/user-identity';

async function authFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${AUTH_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    mode: 'cors',
  });

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');

  let payload: unknown;
  if (isJson) {
    try {
      payload = await response.json();
    } catch {
      payload = await response.text().catch(() => ({ error: 'Failed to parse response' }));
    }
  } else {
    payload = await response.text().catch(() => ({ error: 'Failed to read response' }));
  }

  if (!response.ok) {
    let errorMessage = 'Authentication request failed';

    if (isJson && payload && typeof payload === 'object') {
      if ('message' in payload && typeof (payload as { message: string }).message === 'string') {
        errorMessage = (payload as { message: string }).message;
      } else if ('error' in payload && typeof (payload as { error: string }).error === 'string') {
        errorMessage = (payload as { error: string }).error;
      }
    }

    const error = new Error(errorMessage) as Error & { status?: number; details?: unknown };
    error.status = response.status;
    error.details = payload;
    throw error;
  }

  return payload as T;
}

/**
 * Decode a JWT payload (base64url) without verification.
 * Client-side only -- never trust this for authorization decisions.
 */
export function parseJwt(token: string): AuthUser {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );

  const claims = JSON.parse(jsonPayload);

  return {
    id: claims.sub || claims.id || '',
    email: claims.email || '',
    firstName: claims.firstName || claims.given_name || undefined,
    lastName: claims.lastName || claims.family_name || undefined,
    roles: claims.roles || claims.authorities || [],
    authProvider: claims.authProvider || claims.provider || undefined,
  };
}

// ---------- OIDC User Info ----------

/** Standard OIDC claims returned by GET /oauth2/userinfo */
export interface OidcUserInfo {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  locale?: string;
  zoneinfo?: string;
  picture?: string;
}

/** Shape of GET /api/v1/users/profile from identity service */
export interface IdentityUserProfile {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phoneNumber?: { countryCode?: string; number?: string };
  dateOfBirth?: string;
  gender?: string;
  timezone?: string;
  locale?: string;
  authProvider?: string;
}

// ---------- Local storage (auth store for onboarding / Almost There) ----------
// Contract for five-auth-service (login/reg form): same key + shape + YYYY-MM-DD for dateOfBirth.
// See docs/FIVE_AUTH_SERVICE_LOCAL_STORAGE.md.

export const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

/** Normalize date to YYYY-MM-DD for date inputs and API. Handles ISO strings from auth server. */
export function toDateOnly(value: string | undefined): string {
  if (!value?.trim()) return '';
  const part = value.trim().split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : value.trim();
}

/** User info we keep in localStorage so "Almost There!" and consent can prefill from auth. */
export interface LocalUserInfo {
  signUpEmail?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
}

/**
 * Read current user info from localStorage (auth store).
 * dateOfBirth is normalized to YYYY-MM-DD when present.
 */
export function getLocalUserInfo(): Partial<LocalUserInfo> {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_USER_INFO_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<LocalUserInfo>;
    if (parsed.dateOfBirth) {
      parsed.dateOfBirth = toDateOnly(parsed.dateOfBirth) || parsed.dateOfBirth;
    }
    return parsed;
  } catch {
    return {};
  }
}

/**
 * Merge partial user info into localStorage (auth store).
 * Call after OAuth callback or when the auth server returns identity so "Almost There!" can use it.
 * dateOfBirth is normalized to YYYY-MM-DD before storing.
 */
export function setLocalUserInfo(partial: Partial<LocalUserInfo>): void {
  try {
    if (typeof window === 'undefined') return;
    const existing = getLocalUserInfo();
    const merged: Partial<LocalUserInfo> = { ...existing };
    if (partial.signUpEmail !== undefined) merged.signUpEmail = partial.signUpEmail;
    if (partial.email !== undefined) merged.email = partial.email;
    if (partial.firstName !== undefined) merged.firstName = partial.firstName;
    if (partial.lastName !== undefined) merged.lastName = partial.lastName;
    if (partial.phoneCountryCode !== undefined) merged.phoneCountryCode = partial.phoneCountryCode;
    if (partial.phoneNumber !== undefined) merged.phoneNumber = partial.phoneNumber;
    if (partial.dateOfBirth !== undefined) merged.dateOfBirth = toDateOnly(partial.dateOfBirth) || partial.dateOfBirth;
    if (partial.gender !== undefined) merged.gender = partial.gender;
    localStorage.setItem(LOCAL_USER_INFO_KEY, JSON.stringify(merged));
  } catch {
    // ignore
  }
}

/**
 * Fetch OIDC userinfo from the identity service.
 * Called immediately after token exchange to get name/email for all providers.
 */
export async function fetchOidcUserInfo(accessToken: string): Promise<OidcUserInfo> {
  const response = await fetch(`${AUTH_BASE_URL}/oauth2/userinfo`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    mode: 'cors',
  });
  if (!response.ok) throw new Error(`userinfo ${response.status}`);
  return response.json() as Promise<OidcUserInfo>;
}

/**
 * Fetch user profile from the identity service (/api/v1/users/profile).
 * Used as a fallback in OnboardingConsentPage when localStorage is incomplete.
 */
export async function fetchIdentityProfile(accessToken: string): Promise<IdentityUserProfile> {
  const response = await fetch(`${AUTH_BASE_URL}/api/v1/users/profile`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    mode: 'cors',
  });
  if (!response.ok) {
    // Preserve error.message shape used by AuthContext guards, but attach debugging details.
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    let payload: unknown = null;
    try {
      payload = isJson ? await response.json() : await response.text();
    } catch {
      payload = null;
    }

    const err = new Error(`profile ${response.status}`) as Error & { status?: number; details?: unknown };
    err.status = response.status;
    err.details = payload;
    throw err;
  }
  const payload = await response.json();
  return (payload?.data ?? payload) as IdentityUserProfile;
}

// ---------- API functions ----------

/**
 * Get the OAuth2 authorization URL for a given provider.
 */
export async function getProviderAuthUrl(
  provider: string,
  loginHint?: string
): Promise<{ authorizationUrl: string }> {
  const redirectUri = encodeURIComponent(
    window.location.origin + '/auth/callback/' + provider
  );
  let url = `/oauth2/providers/${provider}/authorize?redirect_uri=${redirectUri}`;
  if (loginHint) {
    url += `&login_hint=${encodeURIComponent(loginHint)}`;
  }
  // Backend returns ApiResponse<OAuth2AuthorizationResponse> — extract data.authorizationUrl
  const response = await authFetch<{ success: boolean; data: { authorizationUrl: string } }>(url);
  return { authorizationUrl: response.data.authorizationUrl };
}

/**
 * Exchange an OAuth2 authorization code for tokens.
 */
export async function exchangeCodeForTokens(
  provider: string,
  code: string,
  state: string
): Promise<TokenResponse> {
  const redirectUri = window.location.origin + '/auth/callback/' + provider;
  // Backend returns ApiResponse<AuthenticationResponse> — extract data
  const response = await authFetch<{ success: boolean; data: TokenResponse }>(
    '/oauth2/callback',
    {
      method: 'POST',
      body: JSON.stringify({ provider, code, state, redirectUri }),
    }
  );
  return response.data;
}

/**
 * Refresh an access token using a refresh token.
 */
export async function refreshToken(
  refreshTokenValue: string
): Promise<TokenResponse> {
  return authFetch<TokenResponse>('/api/v1/auth/token/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });
}

/**
 * Log out the current session. Invalidates the refresh token on the server.
 */
export async function logout(
  accessToken: string,
  refreshTokenValue: string
): Promise<void> {
  return authFetch<void>('/api/v1/auth/logout', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });
}

const DEFAULT_OAUTH_CLIENT_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OAUTH_CLIENT_ID?.trim()) || 'five-web-portal';

/**
 * Password grant at `/api/auth-service/oauth2/token` → access JWT (same nginx route as Docker).
 */
async function fetchPasswordGrantAccessToken(email: string, password: string): Promise<string> {
  const body = new URLSearchParams({
    grant_type: 'password',
    username: email,
    password,
    client_id: DEFAULT_OAUTH_CLIENT_ID,
  });

  const res = await fetch('/api/auth-service/oauth2/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
    mode: 'cors',
  });

  const text = await res.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!res.ok) {
    let msg = 'Password grant failed';
    if (payload && typeof payload === 'object' && 'error_description' in payload) {
      msg = String((payload as { error_description: unknown }).error_description);
    } else if (payload && typeof payload === 'object' && 'message' in payload) {
      msg = String((payload as { message: unknown }).message);
    }
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }

  if (!payload || typeof payload !== 'object' || !('access_token' in payload)) {
    throw new Error('Password grant: missing access_token');
  }
  const access = String((payload as { access_token: unknown }).access_token).trim();
  if (!access) {
    throw new Error('Password grant: empty access_token');
  }
  return access;
}

/**
 * Log in with email / password:
 * 1) OAuth2 password grant (five-auth-service) → JWT
 * 2) user-identity `/auth/login` with `token` = that JWT (response `data` = platform tokens)
 *
 * If identity returns Invalid token for the auth JWT, optionally retries with password in `token`
 * (backends that verify password via five-auth inside identity).
 */
export async function loginWithCredentials(
  email: string,
  password: string
): Promise<TokenResponse> {
  const authJwt = await fetchPasswordGrantAccessToken(email, password);

  const allowPasswordFallback =
    import.meta.env.VITE_IDENTITY_LOGIN_PASSWORD_FALLBACK !== 'false' &&
    import.meta.env.VITE_IDENTITY_LOGIN_PASSWORD_FALLBACK !== '0';

  try {
    const response = await authFetch<{ success?: boolean; data: TokenResponse }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ emailOrUsername: email, token: authJwt }),
    });
    return response.data;
  } catch (e) {
    const err = e as Error & { status?: number; message?: string };
    const isInvalidToken =
      allowPasswordFallback &&
      err.status === 400 &&
      /invalid token/i.test(err.message || '');

    if (!isInvalidToken) {
      throw e;
    }

    const response = await authFetch<{ success?: boolean; data: TokenResponse }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ emailOrUsername: email, token: password }),
    });
    return response.data;
  }
}
